import React, { Fragment } from 'react';
import _ from 'lodash';
import { Translate, useTranslate } from 'core/translate';
import { ButtonText, SecondaryButton } from 'core/controls';
import Date from '../../common/intl/Date';
import Currency from '../../common/intl/Currency';
import {
  Dagger,
  DoubleDagger
} from '../../common/notifications/NotificationsTrigger';
import { normalize as cgiNormalize } from '../../common/cgi/cgi';
import { computePricing } from '../../common/pricing/pricing';
import { withNotifications } from '../../common/notifications/NotificationsContext';
import ShareMathBox, { SHARE_MATH_BOX_ID } from './ShareMathBox';
import ShareImage from './ShareImage';
import FamilyFirstIneligibleMessage from '../../components/mathBox/FamilyFirstIneligibleMessage';

const ShareDescription = ({ config, year, bodyStyleTitle }) => {
  const trimName = _.get(config.style, 'trimName');
  const bodyTypeDescription = _.get(config.bodyType, 'formattedConfig');
  const driveType = _.get(config.driveType, 'id');

  return (
    <div>
      <div className="share__title">
        <Translate
          value="component.price.info.carline.title"
          year={year}
          bodyStyleTitle={bodyStyleTitle}
        />
      </div>
      <div className="share__subtitle">
        <Translate
          value="component.price.info.carline.info"
          driveType={driveType}
          bodyType={bodyTypeDescription}
          trim={trimName}
          year={year}
        />
      </div>
    </div>
  );
};

const getMathBoxNotificationContent = () =>
  document.getElementById(SHARE_MATH_BOX_ID).innerHTML;

const PricingMsrp = withNotifications(
  ({ config, incentives, country, notifications, isFamilyFirstUS }) => {
    const pricing = computePricing(config.msrp, incentives);
    const {
      isSeeDealer,
      employeePrice,
      netEmployeePrice,
      netPrice,
      accessoriesTotal
    } = pricing;
    const { style } = config;
    const { disabled, show, showNamed } = notifications;

    const dagger = disabled ? null : (
      <span className="price-info__dagger">
        <Dagger />
      </span>
    );

    const seeDealerContent = (
      <div className="price-info__msrp-price">
        <span className="price-info__see-dealer">
          <Translate value="unknown.price.text" />
          {dagger}
        </span>
      </div>
    );
    const computerDRMPLabel = () => {
      if (employeePrice) {
        return 'net.employee.price';
      }
      if (!employeePrice) {
        if (pricing.incentives.total || pricing.incentives.value) {
          return 'net.price.after.offers';
        }
      }
      return 'total.vehicle.price';
    };
    const msrpContent = (
      <Fragment>
        <div className="price-info__msrp-price">
          <span className="price-info__discount-price">
            <Currency value={employeePrice ? netEmployeePrice : netPrice} />
          </span>
        </div>
        <div className="price-info__net-price-label">
          {isFamilyFirstUS ? (
            <Translate value={computerDRMPLabel()} />
          ) : (
            <Translate
              value={employeePrice ? 'net.employee.price' : 'net.price'}
            />
          )}
        </div>
        {dagger}
      </Fragment>
    );

    const accessoriesContent = accessoriesTotal ? (
      <div className="save-send__accessories-price">
        <span
          className={disabled ? '' : 'disclaimer'}
          onClick={event => {
            if (!disabled) {
              showNamed('accessories', event);
            }
          }}>
          {' '}
          <span>
            +<Currency value={accessoriesTotal} />{' '}
            {disabled ? null : <DoubleDagger />}{' '}
          </span>
        </span>
        <span>
          <Translate value="accessories.price.label" />
        </span>
      </div>
    ) : null;

    return (
      <Fragment>
        <div
          className={`price-info__msrp-info ${disabled ? '' : 'disclaimer'}`}
          aria-hidden="true"
          onClick={event => {
            if (!disabled) {
              show(getMathBoxNotificationContent(), event);
            }
          }}>
          {isSeeDealer ? seeDealerContent : msrpContent}
        </div>
        {accessoriesContent}
        <FamilyFirstIneligibleMessage
          eligibleForInvoicePrice={style.eligibleForInvoicePrice}
        />
        <ShareMathBox
          pricing={pricing}
          country={country}
          isFamilyFirstUS={isFamilyFirstUS}
          eventName={_.get(style, 'vehicleInfo.promoEventName')}
          inlineIncentives={true}
          isSummary={false}
          eligibleForInvoicePrice={style.eligibleForInvoicePrice}
        />
      </Fragment>
    );
  }
);

const ShareDetails = ({
  config,
  incentives,
  appResources,
  lastModified,
  year,
  bodyStyleTitle,
  country,
  isFamilyFirstUS,
  tabsContent,
  onSelect
}) => {
  const translate = useTranslate();
  if (!config) {
    return <div id="save-send" style={{ width: '100%', minHeight: 500 }} />;
  }

  const images = cgiNormalize(appResources, config, false, '', year);
  const url = _.get(images.exterior[0], 'url');
  const alt = translate('component.price.info.carline.title', {
    bodyStyleTitle,
    year
  });

  return (
    <div id="save-send" style={{ width: '100%', minHeight: 500 }}>
      <div className="save-send__tab-content">
        <div className="save-send__saved-date hide-for-large save-send__tab">
          <Translate value="saved" /> <Date value={lastModified} />
        </div>
        <div className="save-send__vehicle-container">
          <div className="save-send__vehicle-image large-6 small-12 columns">
            <ShareImage url={url} alt={alt} />
          </div>
          <div className="save-send__vehicle-data large-6 small-12 columns">
            <div className="save-send__saved-date show-for-large">
              <Translate value="saved" /> <Date value={lastModified} />
            </div>
            <div className="send-save__price-info">
              <div className="price-info">
                <ShareDescription
                  config={config}
                  year={year}
                  bodyStyleTitle={bodyStyleTitle}
                />
                <PricingMsrp
                  config={config}
                  incentives={incentives}
                  country={country}
                  isFamilyFirstUS={isFamilyFirstUS}
                />
              </div>
            </div>
            <div className="cta-container">
              <SecondaryButton
                className="save-send__cta-button"
                onClick={onSelect}>
                <ButtonText>
                  <Translate value="back.summary" />
                </ButtonText>
              </SecondaryButton>
            </div>
          </div>
        </div>
      </div>
      <div className="save-send__send">
        <div className="save-send__send-text">
          <Translate value="send.link" />:
        </div>
        <div>{tabsContent}</div>
      </div>
    </div>
  );
};

export default ShareDetails;
